import { CreditPlan } from '../model/CreditPlan';

/** Buyer-facing / offer plan kind used for payment display */
export type PlanKind = 'installment' | 'flex';

/** Computed totals for a plan offer given desired net and MDR share */
export interface PlanComputed {
  ok: boolean;
  needsSplit?: boolean;
  share?: number;
  total?: number;
  fee?: number;
  mdrAmt?: number;
  settle?: number;
}

/** A selected plan offer ready to send or display */
export interface SelectedPlanOffer {
  planId: number;
  label: string;
  desc: string;
  kind: PlanKind;
  months: number;
  days: number;
  mdr: number;
  apr: number;
  total: number;
  fee: number;
  /** Buyer MDR share as 0–100, or null when gross-up is off */
  split: number | null;
  custom: boolean;
}

/**
 * Infer plan kind from credit plan metadata.
 */
export function inferPlanKind(
  plan: Pick<CreditPlan, 'name' | 'duration' | 'apr'>,
): PlanKind {
  const name = (plan.name || '').toLowerCase();
  if (name.includes('flex')) {
    return 'flex';
  }
  return 'installment';
}

/**
 * Approximate installment months from plan duration in days.
 */
export function planMonths(
  plan: Pick<CreditPlan, 'duration' | 'name' | 'apr'>,
): number {
  const kind = inferPlanKind(plan);
  if (kind !== 'installment') {
    return 0;
  }
  return plan.duration;
}

/**
 * Monthly payment for an installment loan (standard amortization).
 */
export function monthlyPayment(
  total: number,
  apr: number,
  months: number,
): number {
  if (!months) {
    return total;
  }
  const r = apr / 1200;
  if (!r) {
    return total / months;
  }
  return (total * r) / (1 - Math.pow(1 + r, -months));
}

/**
 * Format a currency amount as $X,XXX.XX
 */
export function formatPlanAmount(n: number): string {
  return (
    '$' +
    Number(n || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

/**
 * Compute grossed-up total for a plan.
 *
 * @param desiredNet Supplier desired net after fees
 * @param mdr Plan MDR as a fraction (e.g. 0.035)
 * @param buyerShare Buyer share of MDR as 0–1, or null when missing
 * @param grossUp Whether gross-up is enabled
 */
export function computePlanTotals(
  desiredNet: number,
  mdr: number,
  buyerShare: number | null,
  grossUp: boolean,
): PlanComputed {
  const net = Number(desiredNet) || 0;
  if (net <= 0) {
    return { ok: false };
  }
  if (!grossUp) {
    const total = net;
    const mdrAmt = total * mdr;
    return {
      ok: true,
      share: 0,
      total,
      fee: 0,
      mdrAmt,
      settle: total - mdrAmt,
    };
  }
  if (buyerShare === null) {
    return { ok: false, needsSplit: true };
  }
  const share = Math.max(0, Math.min(1, buyerShare));
  const denom = 1 - share * mdr;
  if (denom <= 0) {
    return { ok: false, needsSplit: true };
  }
  const total = net / denom;
  const mdrAmt = total * mdr;
  return {
    ok: true,
    share,
    total,
    fee: total - net,
    mdrAmt,
    settle: total - mdrAmt,
  };
}

/**
 * Resolve buyer MDR share (0–1) from default split % and optional override.
 * Returns null when gross-up is on but the effective % is empty/invalid.
 */
export function resolveBuyerShare(
  grossUp: boolean,
  defaultSplitPct: string | number,
  overridePct: string | number | undefined,
): number | null {
  if (!grossUp) {
    return 0;
  }
  const src = overridePct !== undefined ? overridePct : defaultSplitPct;
  if (String(src).trim() === '') {
    return null;
  }
  const n = parseFloat(String(src).replace(/[^0-9.]/g, ''));
  if (isNaN(n)) {
    return null;
  }
  return Math.max(0, Math.min(100, n)) / 100;
}

/**
 * Clamp a percent input to whole numbers 0–100 (empty string preserved).
 */
export function clampPercentInput(v: string | number): string {
  const s = String(v).trim();
  if (s === '') {
    return '';
  }
  const n = Math.round(parseFloat(s.replace(/[^0-9.]/g, '')));
  if (isNaN(n)) {
    return '';
  }
  return String(Math.max(0, Math.min(100, n)));
}

/**
 * Build a SelectedPlanOffer from a CreditPlan and computed totals.
 */
export function toSelectedOffer(
  plan: CreditPlan,
  computed: PlanComputed,
  splitPct: number | null,
  custom: boolean,
): SelectedPlanOffer | null {
  if (!computed.ok || computed.total == null || computed.fee == null) {
    return null;
  }
  return {
    planId: plan.id,
    label: plan.name,
    desc: plan.description || plan.notes || '',
    kind: inferPlanKind(plan),
    months: planMonths(plan),
    days: plan.duration,
    mdr: plan.mdr,
    apr: plan.apr,
    total: Math.round(computed.total * 100) / 100,
    fee: Math.round(computed.fee * 100) / 100,
    split: splitPct,
    custom,
  };
}

/**
 * Due date label for a plan relative to a request date.
 */
export function planDueLabel(
  requestDate: Date | null,
  kind: PlanKind,
  days: number,
  months: number,
): string {
  const base = requestDate ? new Date(requestDate) : new Date();
  if (isNaN(base.getTime())) {
    return '—';
  }
  const offsetDays = kind === 'installment' ? Math.round(months * 30) : days;
  base.setDate(base.getDate() + offsetDays);
  return base.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/**
 * Payment display string for a plan card.
 */
export function planPaymentLabel(
  kind: PlanKind,
  total: number,
  apr: number,
  months: number,
): string {
  if (kind === 'installment') {
    return 'Monthly';
  } else if (kind === 'flex') {
    return 'Pay in Full';
  }
  return '';
}

/**
 * Term display string for a plan.
 */
export function planTermLabel(
  kind: PlanKind,
  days: number,
  months: number,
): string {
  if (kind === 'installment') {
    return months + ' months';
  }
  return days + ' days';
}
